/** Trois blocs lecture : Détail · Informations · Documents. */
import type { ReactNode } from "react";
import { CopyButton } from "../../components/CopyButton";
import { useFormatters } from "../../formats/useFormatters";
import { ClientPortalActions } from "./ClientPortalActions";
import { CopyValue } from "./CopyValue";
import { pcgFull } from "../../pcg/pcg";
import { contactLabel, countryLabel, dash, formatBalance, formatPostalAddress } from "./format";
import { ClientDocuments } from "./ClientDocuments";
import { SireneBadge } from "./SireneBadge";
import { SiretValue } from "./SiretValue";
import type { Client, ClientListItem } from "./types";

type ClientProfileProps = {
  client: Client;
  stats?: Pick<ClientListItem, "balance_cents" | "paid_to_date_cents"> | null;
  busy?: boolean;
  onCheckSirene?: () => void;
  onOpenQuote?: (id: number) => void;
  onOpenInvoice?: (id: number) => void;
};

function Row({
  label,
  children,
  wide,
  tone,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
  tone?: string;
}) {
  return (
    <div className={`fx-fiche-row${wide ? " is-wide" : ""}${tone ? ` is-tone-${tone}` : ""}`}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function siteHref(raw: string): string {
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function AddressBlock({ name, lines }: { name: string; lines: string[] }) {
  const postal = lines.join("\n");
  return (
    <span className="fx-fiche-addr">
      <span className="fx-fiche-addr-lines">
        <strong className="fx-fiche-addr-name">{name}</strong>
        {lines.length === 0 ? (
          <span>—</span>
        ) : (
          lines.map((line, i) => (
            <span key={`${i}-${line}`}>
              {line}
              {i < lines.length - 1 ? <br /> : null}
            </span>
          ))
        )}
      </span>
      {postal ? <CopyButton text={postal} label="Copier l’adresse" /> : null}
    </span>
  );
}

export function ClientProfile({ client, stats, busy, onCheckSirene, onOpenQuote, onOpenInvoice }: ClientProfileProps) {
  const { money, day } = useFormatters();
  const first = (client.contacts ?? [])[0];
  const email = client.email || first?.email || "";
  const phone = client.phone || first?.phone || "";
  const site = (client.website || "").trim();
  const siren = (client.siren || client.routing_id || "").replace(/\D/g, "");
  const vat = (client.vat_number || "").trim();
  const bill = formatPostalAddress({
    line: client.address_line,
    line2: client.address_line2,
    postal_code: client.postal_code,
    city: client.city,
    country_code: client.country_code,
  });
  const ship = formatPostalAddress({
    line: client.ship_address_line,
    line2: client.ship_address_line2,
    postal_code: client.ship_postal_code,
    city: client.ship_city,
    country_code: client.ship_country_code,
  });
  const shipSame =
    ship.join("|") === bill.join("|") ||
    (ship.length === 0 && !client.ship_address_line) ||
    (ship.length === 1 && ship[0] === countryLabel(client.ship_country_code || "FR") && !client.ship_address_line);

  return (
    <div className="fx-fiche-cards">
      <section className="fx-fiche-card">
        <h2>Détail</h2>
        <dl className="fx-fiche-dl">
          <Row label="Contact">{dash(contactLabel(first))}</Row>
          <Row label="E-mail">
            {email ? (
              <CopyValue text={email} label="Copier l’e-mail">
                <a href={`mailto:${email}`}>{email}</a>
              </CopyValue>
            ) : (
              <span className="fx-fiche-mail-miss">Aucun e-mail — la facture ne part pas.</span>
            )}
          </Row>
          <Row label="Téléphone">
            {phone ? <a href={`tel:${phone}`}>{phone}</a> : "—"}
          </Row>
          <Row label="Site">
            {site ? (
              <a href={siteHref(site)} target="_blank" rel="noreferrer">
                {site.replace(/^https?:\/\//i, "")}
              </a>
            ) : (
              "—"
            )}
          </Row>
          <Row label="SIRET">
            <SiretValue value={client.siret} />
          </Row>
          <Row label="INSEE" tone={client.sirene_status || "idle"}>
            <SireneBadge
              status={client.sirene_status}
              checkedAt={client.sirene_checked_at}
              closedOn={client.sirene_closed_on}
              createdOn={client.sirene_created_on}
              action={
                onCheckSirene ? (
                  <button
                    type="button"
                    className="fx-fiche-mini-btn is-ghost"
                    disabled={busy || (client.siret || "").replace(/\D/g, "").length !== 14}
                    onClick={onCheckSirene}
                  >
                    {busy ? "Vérification…" : "Vérifier"}
                  </button>
                ) : null
              }
            />
          </Row>
          <Row label="n° TVA">
            {vat ? (
              <CopyValue text={vat} label="Copier le n° TVA">
                {vat}
              </CopyValue>
            ) : (
              "—"
            )}
          </Row>
          <Row label="SIREN">
            {siren ? (
              <CopyValue text={siren} label="Copier le SIREN">
                {siren}
              </CopyValue>
            ) : (
              "—"
            )}
          </Row>
        </dl>
      </section>
      <section className="fx-fiche-card">
        <h2>Informations</h2>
        <dl className="fx-fiche-dl">
          <Row label="Facturation" wide>
            <AddressBlock name={client.name} lines={bill} />
          </Row>
          {!shipSame && ship.length > 0 ? (
            <Row label="Livraison" wide>
              <AddressBlock name={client.name} lines={ship} />
            </Row>
          ) : null}
          <Row label="Payé à ce jour">{money(stats?.paid_to_date_cents ?? 0)}</Row>
          <Row label="Solde">
            <span className={formatBalance(stats?.balance_cents ?? 0, money).className}>{formatBalance(stats?.balance_cents ?? 0, money).label}</span>
          </Row>
          <Row label="Créé le">{day(client.created_at)}</Row>
          <Row label="TVA valide">{client.vat_valid ? "Oui" : "Non"}</Row>
          <Row label="Dispensé">{client.tax_exempt ? "Oui" : "Non"}</Row>
          <Row label="Compte">{pcgFull(client.pcg_code || "411", client.pcg_sub || "")}</Row>
          <Row
            label="Paiement"
            tone={client.pay?.method === "sepa" && client.pay?.status === "active" ? "sepa" : undefined}
          >
            {client.pay?.label || "Virement"}
          </Row>
        </dl>
        <div className="fx-fiche-card__foot">
          <ClientPortalActions client={client} compact />
        </div>
      </section>
      <ClientDocuments companyId={client.company_id} clientId={client.id} onOpenQuote={onOpenQuote} onOpenInvoice={onOpenInvoice} />
    </div>
  );
}
